import { getFormInitialValues, getFormValues } from 'redux-form';
import { createSelector } from 'reselect';

import { GroupFormFeeding, GroupFormValue } from '../../../routes/FeedGroups/Groups/shared/GroupForm/GroupPartialForm';
import { getRecipes } from '../../entities/recipes/recipes.selector';
import { getUseLockedFeedingsSetting } from '../../entities/settings/settings.selector';
import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { FeedGroupCreationPreview } from './groupPreview.types';

export const getFeedGroupCreationPreview = (state: RootState): FeedGroupCreationPreview =>
  state.application.feedGroups.feedGroupCreationPreview;

export const getUseLockedFeedings = (state: RootState): boolean => state.application.feedGroups.useLockedFeedings;

export const getIsEditMode = (state: RootState): boolean => state.application.feedGroups.isEditMode;

export const canUseLockedFeedings = createSelector(
  [getFeedGroupCreationPreview, getUseLockedFeedingsSetting],
  (groupPreview: FeedGroupCreationPreview, canUseLockedFeedings) => {
    return groupPreview.rationIds?.length === 1 && canUseLockedFeedings;
  },
);

export const hasLockedFeeding = createSelector(getFeedGroupCreationPreview, (groupPreview) =>
  groupPreview.feedings?.some((feeding) => feeding.locked),
);

export const hasOneFeedingSetLocked = createSelector(
  [getFormInitialValues(Forms.groupCreationForm), getFormValues(Forms.groupCreationForm)],
  (initialValues: GroupFormValue, values: GroupFormValue) => {
    const initialLockedFeedingssIndexes = getFilteredFeedingsIndexes(initialValues.feedings, true);
    const lockedFeedingsIndexes = getFilteredFeedingsIndexes(values.feedings, true);

    return initialLockedFeedingssIndexes.length < lockedFeedingsIndexes.length
      ? true
      : lockedFeedingsIndexes.some((elem) => !initialLockedFeedingssIndexes.includes(elem));
  },
);

export const hasOneFeedingSetUnlocked = createSelector(
  [getFormInitialValues(Forms.groupCreationForm), getFormValues(Forms.groupCreationForm)],
  (initialValues: GroupFormValue, values: GroupFormValue) => {
    const initialLockedFeedingssIndexes = getFilteredFeedingsIndexes(initialValues.feedings, true);
    if (initialLockedFeedingssIndexes.length === 0) {
      return false;
    }
    const initialUnlockedFeedingssIndexes = getFilteredFeedingsIndexes(initialValues.feedings, false);
    const unlockedFeedingsIndexes = getFilteredFeedingsIndexes(values.feedings, false);
    return initialUnlockedFeedingssIndexes.length < unlockedFeedingsIndexes.length
      ? true
      : unlockedFeedingsIndexes.some((elem) => !initialUnlockedFeedingssIndexes.includes(elem));
  },
);

export const hasNewFeedingAdded = createSelector(
  [getFormInitialValues(Forms.groupCreationForm), getFormValues(Forms.groupCreationForm)],
  (initialValues: GroupFormValue, values: GroupFormValue) => {
    const initialFeedingsCount = initialValues.feedings.reduce((acc, value) => acc + value.items.length, 0);
    const newFeedingsCount = values.feedings.reduce((acc, value) => acc + value.items.length, 0);
    return initialFeedingsCount !== newFeedingsCount;
  },
);

export const hasTransitonDayRecipe = createSelector(
  [getFeedGroupCreationPreview, getRecipes],
  (groupPreview, recipes) => {
    const feedingRecipeIds = groupPreview.feedings?.map((feeding) => feeding.rationId);
    const feedingsRecipes = recipes.filter((recipe) => feedingRecipeIds?.includes(recipe.id));
    return feedingsRecipes.some(
      (recipe) => recipe.numberOfDays > 1 && typeof recipe.finalDateBeforeTransition === 'undefined',
    );
  },
);

const getFilteredFeedingsIndexes = (feedings: GroupFormFeeding[], locked: boolean) => {
  return feedings
    .map((formRow) => formRow.items)
    .reduce((acc, val) => {
      acc.push(...val);
      return acc;
    }, [])
    .map((rowItem, index) => (rowItem.locked === locked ? index : -1))
    .filter((elem) => elem !== -1);
};
